/**
 * Справочник плановых цен проекта (БМ-11): цена единицы материала без НДС, по которой считаются материалы
 * вариантов «свои силы» (прежде лучших цен КП и поставок компании). Заполняется из КП и поставок, из смет
 * проекта или вручную; у каждой строки видно, откуда цена и сколько она в сметах.
 */
import {
  PRICE_SOURCES,
  type ProjectPrice,
  type ProjectPriceInput,
  type ProjectPricesFillResult,
  type ProjectPricesView,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

export const planPricesQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['budget', 'plan-prices', projectId],
    queryFn: () => api<ProjectPricesView>(`/projects/${projectId}/plan-prices`),
  });

const BTN = 'min-h-[44px] md:min-h-0';
const qty = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 4 });
const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  const w = m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? few : many;
  return `${n} ${w}`;
};

type Draft = ProjectPriceInput;

function PriceDialog({
  projectId,
  initial,
  onClose,
}: {
  projectId: string;
  initial: { id: string | null; d: Draft };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(initial.d);
  const m = useMutation({
    mutationFn: () =>
      initial.id
        ? api<ProjectPricesView>(`/projects/${projectId}/plan-prices/${initial.id}`, {
            method: 'PATCH',
            body: d,
          })
        : api<ProjectPricesView>(`/projects/${projectId}/plan-prices`, { body: d }),
    onSuccess: (data) => {
      qc.setQueryData(planPricesQuery(projectId).queryKey, data);
      qc.invalidateQueries({ queryKey: ['budget'] });
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={initial.id ? 'Плановая цена' : 'Новая плановая цена'}
      description="Цена единицы без НДС. Материалы вариантов «свои силы» по лучшим ценам считаются сначала по этому справочнику, потом по КП и поставкам компании."
      footer={
        <>
          <Button className={BTN} variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className={BTN}
            variant="primary"
            loading={m.isPending}
            disabled={d.name.trim().length < 2 || !d.unit.trim() || !(d.priceNet >= 0)}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Материал" className="sm:col-span-2">
          <Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        </Field>
        <Field label="Единица">
          <Input value={d.unit} onChange={(e) => setD({ ...d, unit: e.target.value })} />
        </Field>
        <Field label="Цена без НДС, ₸">
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={Number.isFinite(d.priceNet) ? d.priceNet : ''}
            onChange={(e) =>
              setD({ ...d, priceNet: e.target.value === '' ? Number.NaN : Number(e.target.value) })
            }
          />
        </Field>
        <Field label="Примечание" className="sm:col-span-2">
          <Textarea
            rows={2}
            value={d.note ?? ''}
            onChange={(e) => setD({ ...d, note: e.target.value || null })}
          />
        </Field>
      </div>
    </Dialog>
  );
}

export function PlanPricesPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const q = useQuery(planPricesQuery(projectId));
  const [editing, setEditing] = useState<{ id: string | null; d: Draft } | null>(null);
  const [showMissing, setShowMissing] = useState(false);
  const refresh = (data?: ProjectPricesView) => {
    if (data) qc.setQueryData(planPricesQuery(projectId).queryKey, data);
    else qc.invalidateQueries({ queryKey: planPricesQuery(projectId).queryKey });
    qc.invalidateQueries({ queryKey: ['budget'] });
  };
  const fill = useMutation({
    mutationFn: (body: { from: 'offers' | 'estimate'; overwrite?: boolean }) =>
      api<ProjectPricesFillResult>(`/projects/${projectId}/plan-prices/fill`, { body }),
    onSuccess: (r) => {
      refresh();
      toast(
        `Добавлено ${r.added}, обновлено ${r.updated}, пропущено ${r.skipped}`,
        r.added || r.updated ? 'success' : 'info',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) =>
      api<ProjectPricesView>(`/projects/${projectId}/plan-prices/${id}`, { method: 'DELETE' }),
    onSuccess: (data) => refresh(data),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const toDraft = (p: ProjectPrice): Draft => ({
    name: p.name,
    unit: p.unit,
    priceNet: p.priceNet ?? 0,
    note: p.note,
  });
  const delta = (p: ProjectPrice) =>
    p.estimatePrice && p.priceNet !== null && p.estimatePrice > 0
      ? Math.round(((p.priceNet - p.estimatePrice) / p.estimatePrice) * 1000) / 10
      : null;
  return (
    <Panel
      title="Плановые цены проекта"
      meta={`БМ-11: ${plural(v.items.length, 'цена', 'цены', 'цен')} без НДС, ${v.missing.length} материалов смет без цены`}
      bodyClassName="p-0"
      actions={
        v.canEdit ? (
          <>
            <Button
              className={BTN}
              size="sm"
              variant="ghost"
              loading={fill.isPending && fill.variables?.from === 'offers'}
              onClick={() => {
                const hasOffers = v.items.some((p) => p.source === 'offer' || p.source === 'receipt');
                const overwrite =
                  hasOffers &&
                  window.confirm(
                    'Обновить и цены, взятые раньше из КП и поставок? «Отмена» — только добавить недостающие. Ручные цены и цены из смет не трогаются.',
                  );
                fill.mutate({ from: 'offers', overwrite });
              }}
              title="Последняя выбранная, иначе минимальная цена похожих КП и поставок компании; ручные цены и цены из смет не трогаются"
            >
              Из КП и поставок
            </Button>
            <Button
              className={BTN}
              size="sm"
              variant="ghost"
              loading={fill.isPending && fill.variables?.from === 'estimate'}
              onClick={() => fill.mutate({ from: 'estimate' })}
              title="Цена ресурса в сметах проекта — для материалов, у которых плановой цены ещё нет"
            >
              Из смет
            </Button>
            <Button
              className={BTN}
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() =>
                setEditing({ id: null, d: { name: '', unit: 'шт.', priceNet: Number.NaN, note: null } })
              }
            >
              Цена
            </Button>
          </>
        ) : null
      }
    >
      {v.items.length ? (
        <ul className="divide-y divide-line">
          {v.items.map((p) => {
            const dl = delta(p);
            return (
              <li
                key={p.id}
                className="flex flex-col gap-1 px-5 py-2.5 text-[13px] md:flex-row md:items-center md:gap-4"
              >
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                  <span className="font-medium">{p.name}</span>, {p.unit}{' '}
                  <Badge>{PRICE_SOURCES[p.source]}</Badge>
                  <span className="block text-[12px] text-ink-2">
                    {p.sourceNote ? `${p.sourceNote} · ` : ''}
                    {p.estimateQuantity !== null
                      ? `в сметах ${qty(p.estimateQuantity)} ${p.unit} по ${tenge(p.estimatePrice)}`
                      : 'в сметах проекта не встречается'}
                    {p.note ? ` · ${p.note}` : ''}
                  </span>
                </span>
                <span
                  className={clsx(
                    'num text-[12px]',
                    dl === null
                      ? 'text-ink-3'
                      : dl < 0
                        ? 'text-success'
                        : dl > 0
                          ? 'text-danger'
                          : 'text-ink-2',
                  )}
                >
                  {dl === null ? '' : `${dl > 0 ? '+' : ''}${dl} % к смете`}
                </span>
                <span className="num w-[130px] font-medium md:text-right">{tenge(p.priceNet)}</span>
                {v.canEdit ? (
                  <span className="flex gap-2">
                    <Button
                      className={BTN}
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ id: p.id, d: toDraft(p) })}
                    >
                      Изменить
                    </Button>
                    <Button
                      className={BTN}
                      size="sm"
                      variant="ghost"
                      loading={del.isPending && del.variables === p.id}
                      onClick={() => {
                        if (window.confirm(`Убрать плановую цену «${p.name}»?`)) del.mutate(p.id);
                      }}
                    >
                      Удалить
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-5 py-3 text-[13px] text-ink-2">
          Плановых цен пока нет: заполните из КП и поставок компании, из смет проекта или добавьте вручную —
          по ним считаются материалы вариантов «свои силы».
        </p>
      )}
      {v.missing.length ? (
        <div className="border-t border-line px-5 py-2.5 text-[13px]">
          <button
            type="button"
            className={clsx(BTN, 'text-accent-text underline-offset-2 hover:underline')}
            onClick={() => setShowMissing((x) => !x)}
          >
            {showMissing
              ? 'Скрыть материалы без цены'
              : `Материалы смет без плановой цены (${v.missing.length})`}
          </button>
          {showMissing ? (
            <ul className="mt-2 divide-y divide-line rounded-md border border-line text-[12px]">
              {v.missing.slice(0, 100).map((m) => (
                <li
                  key={`${m.name}|${m.unit}`}
                  className="flex flex-col gap-1 px-3 py-2 md:flex-row md:items-center md:gap-3"
                >
                  <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                    {m.name}, {m.unit} × {qty(m.quantity)}
                  </span>
                  <span className="num text-ink-2">смета {tenge(m.estimatePrice)}</span>
                  {v.canEdit ? (
                    <Button
                      className={BTN}
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setEditing({
                          id: null,
                          d: {
                            name: m.name,
                            unit: m.unit,
                            priceNet: m.estimatePrice ?? Number.NaN,
                            note: null,
                          },
                        })
                      }
                    >
                      Задать цену
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {editing ? (
        <PriceDialog projectId={projectId} initial={editing} onClose={() => setEditing(null)} />
      ) : null}
    </Panel>
  );
}
